import React, { useEffect, useState } from 'react';
import { Euro, Plus, X } from 'lucide-react';
import { Section, Input, Select, Checkbox, Switch } from '../ui';
import { RangeSlider } from '../ui/RangeSlider';
import { FreeTierHint } from './FreeTierHint';
import type { ValidationIssue } from '../../utils/subscriptionValidation';
import type { SubscriptionFilters } from '../SubscriptionEditorFields';

export interface WhatGroupProps {
  density?: 'default' | 'compact';
  filters: SubscriptionFilters;
  issues: ValidationIssue[];
  isLoading: boolean;
  onFilterChange: (field: string, value: string) => void;
  isBaseRentActive: boolean;
  onBaseRentToggle: (active: boolean) => void;
  showFreeHint: boolean;
}

const ROOM_OPTIONS: Array<[string, string]> = [
  ['1', '1'], ['1.5', '1,5'], ['2', '2'], ['2.5', '2,5'], ['3', '3'], ['3.5', '3,5'], ['4', '4'], ['5', '5+'],
];
const MAX_ROOM_OPTIONS: Array<[string, string]> = [
  ['1', '1'], ['1.5', '1,5'], ['2', '2'], ['2.5', '2,5'], ['3', '3'], ['3.5', '3,5'], ['4', '4'],
  ['5', '5'], ['6', '6'], ['8', '8'], ['10', '10'],
];

// Slider ranges as on main; the slider end means "no limit".
const WARM_MAX = 5000;
const COLD_MAX = 4000;
const RENT_STEP = 50;

interface RentRangeProps {
  id: string;
  label: string;
  minField: keyof SubscriptionFilters;
  maxField: keyof SubscriptionFilters;
  sliderMax: number;
  filters: SubscriptionFilters;
  onFilterChange: (field: string, value: string) => void;
  getIssue: (field: keyof SubscriptionFilters) => string | undefined;
  disabled: boolean;
  compact: boolean;
}

/**
 * One rent bound as slider + narrow number field ("bis"). "+ Mindestwert" reveals the
 * "ab" bound, turning the slider into a two-thumb range; it starts revealed when a
 * saved search already has one.
 */
const RentRange: React.FC<RentRangeProps> = ({ id, label, minField, maxField, sliderMax, filters, onFilterChange, getIssue, disabled, compact }) => {
  const minValue = filters[minField] as string;
  const maxValue = filters[maxField] as string;
  // A stored 0 means "no minimum" (the API sends 0 for unset bounds).
  const hasMin = !!minValue && parseFloat(minValue) > 0;
  const [showMin, setShowMin] = useState(hasMin);
  // Saved filters arrive after the first render when editing.
  useEffect(() => {
    if (hasMin) setShowMin(true);
  }, [hasMin]);
  const error = getIssue(minField) || getIssue(maxField);

  // Input puts its rightSlot in a full-width wrapper, so the width lives on this wrapper.
  const fieldWidth = compact ? 'min-w-0 flex-1' : 'min-w-0 flex-1 sm:w-36 sm:flex-none';
  const numberInput = (field: keyof SubscriptionFilters, value: string, name: string) => (
    <div className={fieldWidth}>
      <Input
        id={field}
        type="number"
        min={0}
        step={RENT_STEP}
        aria-label={name}
        placeholder="Beliebig"
        rightSlot={<span className="text-muted">€</span>}
        value={value}
        onChange={(e) => onFilterChange(field, e.target.value)}
        disabled={disabled}
        invalid={!!getIssue(field)}
        className="tabular"
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={maxField} className="text-sm font-medium text-ink">
          {label} {showMin ? '' : 'bis'}
        </label>
        {showMin ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink disabled:opacity-50"
            onClick={() => {
              onFilterChange(minField, '');
              setShowMin(false);
            }}
            disabled={disabled}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" /> Mindestwert entfernen
          </button>
        ) : (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline disabled:opacity-50"
            onClick={() => setShowMin(true)}
            disabled={disabled}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Mindestwert
          </button>
        )}
      </div>
      <div className={compact ? 'flex flex-col gap-4' : 'flex flex-col gap-4 sm:flex-row sm:items-center'}>
        {/* flex-1 only in the row layout: in the column layout its 0 basis collapsed the slider's height. */}
        <RangeSlider
          className={compact ? 'min-w-0' : 'min-w-0 sm:flex-1'}
          min={0}
          max={sliderMax}
          step={RENT_STEP}
          high={maxValue}
          onHighChange={(v) => onFilterChange(maxField, v)}
          highLabel={`${label} bis`}
          {...(showMin ? { low: minValue, onLowChange: (v: string) => onFilterChange(minField, v), lowLabel: `${label} ab` } : {})}
          disabled={disabled}
        />
        <div id={`${id}-fields`} className={compact ? 'flex items-center gap-2' : 'flex items-center gap-2 sm:shrink-0'}>
          {showMin && (
            <>
              {numberInput(minField, minValue, `${label} ab`)}
              <span className="text-muted" aria-hidden="true">–</span>
            </>
          )}
          {numberInput(maxField, maxValue, `${label} bis`)}
        </div>
      </div>
      {error && (
        <p role="alert" className="text-sm text-danger-text">{error}</p>
      )}
    </div>
  );
};

/** "Was" group: Warm-/Kaltmiete (slider + fields, optional minimum), Zimmer and Fläche. */
export const WhatGroup: React.FC<WhatGroupProps> = ({
  density = 'default',
  filters,
  issues,
  isLoading,
  onFilterChange,
  isBaseRentActive,
  onBaseRentToggle,
  showFreeHint,
}) => {
  const getIssue = (field: keyof SubscriptionFilters) => issues.find((i) => i.field === field)?.message;
  const compact = density === 'compact';
  const rangeProps = { filters, onFilterChange, getIssue, disabled: isLoading, compact };
  const pairInput = (field: keyof SubscriptionFilters, name: string) => (
    <Input
      id={field}
      type="number"
      min={0}
      aria-label={name}
      placeholder="Beliebig"
      rightSlot={<span className="text-muted">m²</span>}
      value={filters[field] as string}
      onChange={(e) => onFilterChange(field, e.target.value)}
      disabled={isLoading}
      invalid={!!getIssue(field)}
      className="tabular"
    />
  );

  const content = (
    <>
      {showFreeHint && <FreeTierHint />}

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <RentRange id="warm" label="Warmmiete" minField="minRent" maxField="maxRent" sliderMax={WARM_MAX} {...rangeProps} />
          <Checkbox
            label="Auch bei fehlender oder unklarer Miete"
            description="z. B. „Miete auf Anfrage“ oder nur Kaltmiete angegeben"
            checked={filters.includeUnknowns}
            onChange={(e) => onFilterChange('includeUnknowns', e.target.checked as any)}
            disabled={isLoading}
          />
        </div>

        <div className="flex flex-col gap-3">
          <Switch
            label="Kaltmiete zusätzlich begrenzen"
            description="Liegt die Kaltmiete über der Warmmiete-Grenze, wird die Warmmiete-Grenze automatisch mit angehoben."
            checked={isBaseRentActive}
            onCheckedChange={onBaseRentToggle}
            disabled={isLoading}
          />
          {isBaseRentActive && (
            <RentRange id="cold" label="Kaltmiete" minField="minBaseRent" maxField="maxBaseRent" sliderMax={COLD_MAX} {...rangeProps} />
          )}
        </div>

        <div className={compact ? 'flex flex-col gap-4' : 'grid gap-4 sm:grid-cols-2'}>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="minRooms" className="text-sm font-medium text-ink">Zimmer</label>
            <div className="flex items-center gap-2">
              <Select id="minRooms" aria-label="Zimmer ab" value={filters.minRooms} onChange={(e) => onFilterChange('minRooms', e.target.value)} disabled={isLoading}>
                <option value="">ab …</option>
                {ROOM_OPTIONS.map(([value, optionLabel]) => (
                  <option key={value} value={value}>{optionLabel}</option>
                ))}
              </Select>
              <span className="text-muted" aria-hidden="true">–</span>
              <Select id="maxRooms" aria-label="Zimmer bis" value={filters.maxRooms} onChange={(e) => onFilterChange('maxRooms', e.target.value)} disabled={isLoading}>
                <option value="">bis …</option>
                {MAX_ROOM_OPTIONS.map(([value, optionLabel]) => (
                  <option key={value} value={value}>{optionLabel}</option>
                ))}
              </Select>
            </div>
            {getIssue('minRooms') && <p role="alert" className="text-sm text-danger-text">{getIssue('minRooms')}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="minSpace" className="text-sm font-medium text-ink">Fläche</label>
            <div className="flex items-center gap-2">
              {pairInput('minSpace', 'Fläche ab')}
              <span className="text-muted" aria-hidden="true">–</span>
              {pairInput('maxSpace', 'Fläche bis')}
            </div>
            {(getIssue('minSpace') || getIssue('maxSpace')) && (
              <p role="alert" className="text-sm text-danger-text">{getIssue('minSpace') || getIssue('maxSpace')}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );

  if (density === 'compact') {
    // Native Telegram list convention: small muted uppercase section title (allowed only on /tg).
    return (
      <div className="flex flex-col gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Was</h2>
        {content}
      </div>
    );
  }

  return (
    <Section title="Was" icon={<Euro className="h-5 w-5" aria-hidden="true" />}>
      {content}
    </Section>
  );
};
